import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { ApiFinance } from "./ApiFinance";
import { GetLanguageString } from "../helper/Components";

const getDefaultShareMonthDate = () => {
  const today = new Date();
  return today.getDate() <= 20
    ? new Date(today.getFullYear(), today.getMonth() - 1, 1)
    : new Date(today.getFullYear(), today.getMonth(), 1);
};

const ShareDateExtensionModal = ({
  isOpen,
  toggle,
  unitId,
  shareType, // "halqa" or "markaz"
  shareMonthDate, // Date object or string
  onSuccess,
}) => {
  const { t } = useTranslation();

  const [requestedDate, setRequestedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  });
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const effectiveShareMonthDate = shareMonthDate || getDefaultShareMonthDate();

  const getFormattedShareMonth = () => {
    const dateObj =
      effectiveShareMonthDate instanceof Date
        ? effectiveShareMonthDate
        : new Date(effectiveShareMonthDate);
    if (isNaN(dateObj.getTime())) return String(effectiveShareMonthDate);
    const monthName = dateObj.toLocaleString("en-US", { month: "long" });
    const year = dateObj.getFullYear();
    return `${monthName} ${year}`;
  };

  const getShareMonthString = () => {
    const dateObj =
      effectiveShareMonthDate instanceof Date
        ? effectiveShareMonthDate
        : new Date(effectiveShareMonthDate);
    if (isNaN(dateObj.getTime())) return String(effectiveShareMonthDate);
    return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
  };

  const validate = () => {
    const errs = {};
    if (!requestedDate) {
      errs.requestedDate = t("common_field_required_error", "This field is required");
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (requestedDate < today) {
        errs.requestedDate = t(
          "funds_date_extension_future_date_required",
          "Requested extended date must be today or in the future."
        );
      }
    }

    if (!reason || !reason.trim()) {
      errs.reason = t("common_field_required_error", "This field is required");
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        unitId: parseInt(unitId, 10),
        shareType: (shareType || "halqa").toLowerCase(),
        shareMonth: getShareMonthString(),
        requestedUntilDate: requestedDate.toISOString(),
        reason: reason.trim(),
      };

      const res = await ApiFinance().RequestShareDateExtension(payload);

      if (res && (res.status === 200 || res.data?.statusCode === 200)) {
        const responseMsg = res.data?.message;
        const displayMsg = responseMsg
          ? (t(responseMsg) !== responseMsg ? t(responseMsg) : responseMsg)
          : t(
              "funds_date_extension_request_success",
              "Date extension request submitted successfully."
            );
        swal({
          title: t("common_success", "Success"),
          text: displayMsg,
          icon: "success",
          button: t("common_ok", "OK"),
        }).then(() => {
          toggle();
          if (onSuccess) onSuccess();
        });
      } else {
        swal({
          title: t("common_warning", "Warning"),
          text: res?.data?.message || t("common_something_went_wrong", "Something went wrong."),
          icon: "warning",
          button: t("common_ok", "OK"),
        });
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        t("common_something_went_wrong", "Something went wrong.");
      swal({
        title: t("common_error", "Error"),
        text: errMsg,
        icon: "error",
        button: t("common_ok", "OK"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={loading ? undefined : toggle}
      className="inmodal"
      autoFocus={false}
    >
      <ModalHeader className="modal-title" tag="h4">
        <GetLanguageString props="funds_request_date_extension_title" />
      </ModalHeader>
      <Form onSubmit={handleSubmit} noValidate>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="col-form-label">
                  <GetLanguageString props="funds_share_month" />
                </Label>
                <Input
                  type="text"
                  value={getFormattedShareMonth()}
                  disabled
                  readOnly
                  className="form-control"
                />
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label className="col-form-label">
                  <GetLanguageString props="funds_requested_extended_date" />{" "}
                  <span className="text-danger">*</span>
                </Label>
                <DatePicker
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="dd/mm/yyyy"
                  selected={requestedDate}
                  onChange={(d) => {
                    setRequestedDate(d);
                    if (errors.requestedDate) {
                      setErrors((prev) => ({ ...prev, requestedDate: "" }));
                    }
                  }}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                />
                {errors.requestedDate && (
                  <h4 className="text-error">{errors.requestedDate}</h4>
                )}
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label className="col-form-label">
                  <GetLanguageString props="funds_extension_reason" />{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (errors.reason) {
                      setErrors((prev) => ({ ...prev, reason: "" }));
                    }
                  }}
                  placeholder={t(
                    "funds_extension_reason_placeholder",
                    "Enter detailed reason for requesting date extension..."
                  )}
                  className="form-control"
                />
                {errors.reason && (
                  <h4 className="text-error">{errors.reason}</h4>
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="white" onClick={toggle} disabled={loading}>
            <GetLanguageString props="common_cancel" />
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <GetLanguageString props="funds_request_date_extension_btn" />
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ShareDateExtensionModal;
